import { useState } from 'react';
import { Button, MenuItem, Stack, styled, TextField, Typography } from '@mui/material';
import {
  DesktopDateTimePicker,
  LocalizationProvider,
  TimeField as TimeFieldMui,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { Instruction, Schedule, ScheduleAt } from 'types/task';

import { WEEKDAYS } from '../../constants/scheduleOptions';

interface IInstructionFormProps {
  value: string;
  onChange: (e: keyof Instruction, value: string | ScheduleAt) => void;
  onChangeSchedule: (e: keyof Instruction, value: Schedule) => void;
  schedule: Schedule;
  onSubmit: () => void;
  isLoading?: boolean;
  buttonTitle: string;
  instruction: Instruction;
  title: string;
  scheduleOptions: {
    label: string;
    value: Schedule;
  }[];
}

const TimeField = styled(TimeFieldMui)`
  width: 100%;

  .MuiPickersSectionList-root {
    padding: 13.5px 0;
  }

  fieldset {
    border-color: ${({ theme }) => theme.palette.neutral[200]};
  }
`;

interface SchedulePicker {
  value: ScheduleAt;
  onChange: (key: string, value: number | Dayjs | string) => void;
}

const schedulePickerComponents = {
  [Schedule.DAILY]: ({ onChange, value }: SchedulePicker) => (
    <TimeField
      label="Time"
      onChange={(time) => onChange('schedule_time', time)}
      value={value.schedule_time}
      ampm={false}
      format="HH:mm"
    />
  ),
  [Schedule.WEEKLY]: ({ onChange, value }: SchedulePicker) => (
    <Stack
      spacing={2}
      direction="row"
      sx={{
        width: '100%',
      }}
    >
      <TextField
        select
        fullWidth
        name="weekday_number"
        defaultValue={new Date().getDay()}
        value={value.weekday_number}
        onChange={(e) => onChange('weekday_number', e.target.value)}
        variant="outlined"
        label="Weekday"
      >
        {WEEKDAYS.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
          >
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      <TimeField
        label="Time"
        onChange={(time) => onChange('schedule_time', time)}
        name="schedule_time"
        value={value.schedule_time}
        ampm={false}
      />
    </Stack>
  ),
  [Schedule.MONTHLY]: ({ onChange, value }: SchedulePicker) => (
    <Stack
      spacing={2}
      direction="row"
      sx={{
        width: '100%',
      }}
    >
      <TextField
        select
        fullWidth
        defaultValue={new Date().getDate()}
        value={value.month_day}
        name="month_day"
        onChange={(e) => onChange('month_day', e.target.value)}
        variant="outlined"
        label="Day"
      >
        {Array.from({ length: 31 }, (_, i) => i + 1).map((option) => (
          <MenuItem
            key={option}
            value={option}
          >
            {option}
          </MenuItem>
        ))}
      </TextField>
      <TimeField
        label="Time"
        name="schedule_time"
        onChange={(time) => onChange('schedule_time', time)}
        value={value.schedule_time}
        ampm={false}
      />
    </Stack>
  ),
  [Schedule.NOW]: () => <></>,
  [Schedule.HOURLY]: () => <></>,
  [Schedule.EVERY_MINUTE]: () => <></>,
  [Schedule.CUSTOM_TIME]: () => <></>,
};

export const InstructionForm = ({
  value,
  onChange,
  instruction,
  schedule,
  onSubmit,
  isLoading,
  buttonTitle,
  title,
  scheduleOptions,
  onChangeSchedule,
}: IInstructionFormProps) => {
  const [isError, setIsError] = useState(false);

  const change = (key: string, value: number | Dayjs | string) => {
    onChange('scheduleAt', { ...instruction.scheduleAt, [key]: value });
  };

  const handleSubmit = () => {
    if (!value || !schedule) {
      setIsError(true);
    }
    onSubmit?.();
  };

  return (
    <Stack
      alignItems="flex-start"
      gap={2}
    >
      <Typography variant="h5">{title}</Typography>
      <TextField
        fullWidth
        placeholder="Enter instruction (e.g., Generate a weekly report)"
        value={value}
        onChange={(e) => onChange('instruction', e.target.value)}
        variant="outlined"
        multiline
        minRows={3}
        maxRows={6}
        error={isError && !value}
        sx={{
          height: 'auto',
        }}
      />
      <TextField
        select
        fullWidth
        defaultValue="all"
        value={schedule}
        onChange={(e) => onChangeSchedule('schedule', e.target.value as Schedule)}
        variant="outlined"
        error={isError && !schedule}
      >
        {scheduleOptions.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
          >
            {option.label}
          </MenuItem>
        ))}
      </TextField>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        {schedulePickerComponents[schedule]?.({
          onChange: change,
          value: instruction.scheduleAt || {},
        })}
      </LocalizationProvider>

      {schedule === Schedule.CUSTOM_TIME && (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DesktopDateTimePicker
            sx={{
              width: '100%',
              '.MuiPickersSectionList-root': {
                padding: '15px 0',
              },

              fieldset: { borderColor: '#E5E7EB' },
            }}
            minDateTime={dayjs(Date.now())}
            ampm={false}
            onChange={(date) => change('schedule_time', date)}
          />
        </LocalizationProvider>
      )}
      <Button
        variant="contained"
        size="large"
        onClick={handleSubmit}
        loading={isLoading}
        disabled={isLoading}
      >
        {buttonTitle}
      </Button>
    </Stack>
  );
};
